import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { AudioPlayer } from 'src/components/AudioPlayer';
import { Badge } from 'src/components/Badge';
import { Box } from 'src/components/Box';
import { CircularProgress } from 'src/components/CircularProgress';
import { Icon } from 'src/components/Icon';
import { Typography } from 'src/components/Typography';
import { ImageVideoPreviewUpload } from 'src/models/conversation/_types';

interface UploadPreviewProps {
  uploads: ImageVideoPreviewUpload[];
  uploading: boolean;
  handleCancelClick: (upload: ImageVideoPreviewUpload) => void;
}

export const UploadPreview: React.FC<UploadPreviewProps> = ({
  uploads,
  uploading,
  handleCancelClick,
}) => {
  return (
    <>
      {!!uploads.length && (
        <Box sx={{ p: 1.5, pt: 1, borderRadius: 1 }}>
          {uploads.map((upload, index) => (
            <Badge
              key={index}
              icon={icon({ name: 'times', style: 'regular' })}
              sx={{ cursor: 'pointer', mr: 2 }}
              onClick={() => handleCancelClick(upload)}
            >
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                {uploading && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    width="100%"
                    sx={{ position: 'absolute' }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size="medium"
                      color="secondary"
                      thickness={4}
                      sx={{
                        position: 'absolute',
                        zIndex: 1,
                        opacity: 0.3,
                        color: (theme) => theme.palette.primary[50],
                      }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={upload.progress}
                      size="medium"
                      thickness={4}
                      sx={{
                        position: 'absolute',
                        zIndex: 1,
                        color: (theme) => theme.palette.primary[300],
                      }}
                    />
                  </Box>
                )}
                {upload.preview &&
                  (upload.file.type.startsWith('video/') ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          bgcolor: 'rgba(0,0,0,0.6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                        }}
                      >
                        <Icon
                          icon={icon({
                            name: 'video',
                            style: 'regular',
                          })}
                          sx={{ color: 'white' }}
                        />
                      </Box>
                      <Box
                        component="video"
                        src={upload.preview}
                        autoPlay
                        muted
                        sx={{
                          display: 'block',
                          height: 60,
                          width: 60,
                          objectFit: 'cover',
                          borderRadius: 1,
                          borderWidth: 1,
                          borderColor: 'divider',
                          borderStyle: 'solid',
                        }}
                      />
                    </Box>
                  ) : upload.file.type.startsWith('audio/') ? (
                    <AudioPlayer url={upload.preview} />
                  ) : upload.file.type.startsWith('image/') ? (
                    <Box
                      component="img"
                      src={upload.preview}
                      alt="preview"
                      sx={{
                        display: 'block',
                        height: 60,
                        width: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        borderWidth: 1,
                        borderColor: 'divider',
                        borderStyle: 'solid',
                      }}
                    />
                  ) : (
                    <Box
                      p={2}
                      sx={{
                        maxWidth: '100%',
                        display: 'block',
                        borderRadius: 1,
                        borderWidth: 1,
                        borderColor: 'divider',
                        borderStyle: 'solid',
                        bgcolor: 'background.level1',
                      }}
                    >
                      <Typography>{upload.preview}</Typography>
                    </Box>
                  ))}
              </Box>
            </Badge>
          ))}
        </Box>
      )}
    </>
  );
};
